import React, { useState, useEffect, useMemo } from "react";
import { ExtractedContract } from "../../types/contract";

interface UseBuildingParams {
  originalContracts: ExtractedContract[];
  isTrashView: boolean;
  dataMode: 'full' | 'paginated' | null;
  sortOrder: string;
  itemsPerPage: number;
  pagination: any;
  propertyFilter: string;
  roomFilter: string;
  lesseeFilter: string;
  pageScrollRef: React.RefObject<HTMLDivElement | null>;
  detailContentRef: React.RefObject<HTMLDivElement | null>;
}

export const useBuilding = ({
  originalContracts,
  isTrashView,
  dataMode,
  sortOrder,
  itemsPerPage,
  pagination,
  propertyFilter,
  roomFilter,
  lesseeFilter,
  pageScrollRef,
  detailContentRef,
}: UseBuildingParams) => {
  const [editableContracts, setEditableContracts] = useState<ExtractedContract[]>([]);
  const [aggregatedContracts, setAggregatedContracts] = useState<ExtractedContract[]>([]);
  const [selectedContract, setSelectedContract] = useState<ExtractedContract | null>(null);
  const [clientCurrentPage, setClientCurrentPage] = useState<number>(1);

  // itemsPerPageの変更を検知してクライアントページをリセット
  useEffect(() => {
    setClientCurrentPage(1);
  }, [itemsPerPage]);

  const originalContractsString = useMemo(() => JSON.stringify(originalContracts), [originalContracts]);

  useEffect(() => {
    if (!isTrashView) {
        // Deep copy from context to local editable state
        setEditableContracts(JSON.parse(originalContractsString));
    }
  }, [originalContractsString, isTrashView]);

  // ローカルフィルタリングとデータ表示
  useEffect(() => {
    // パスB（ページング）の場合は、バックエンドでフィルタリング済み
    // バックエンドから既に正しい順序で取得されているので、そのまま表示
    if (dataMode === 'paginated' && !isTrashView) {
      setAggregatedContracts(editableContracts);
      return;
    }

    // パスA（全件取得）またはゴミ箱の場合は、ローカルでフィルタリング
    const lowerCasePropertyFilter = (propertyFilter || "").toLowerCase();
    const lowerCaseRoomFilter = (roomFilter || "").toLowerCase();
    const lowerCaseLesseeFilter = (lesseeFilter || "").toLowerCase();

    const filteredC = editableContracts.filter(
      (c) =>
        (c.property_name || "")
          .toLowerCase()
          .includes(lowerCasePropertyFilter) &&
        (c.room_name || "").toLowerCase().includes(lowerCaseRoomFilter) &&
        (c.lessee_name || "").toLowerCase().includes(lowerCaseLesseeFilter)
    );

    // ソート順に応じて並び替え
    const sorted = sortOrder === 'desc'
      ? [...filteredC].sort((a, b) => (b.id ?? 0) - (a.id ?? 0))
      : [...filteredC].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));

    setAggregatedContracts(sorted);
  }, [
    propertyFilter,
    roomFilter,
    lesseeFilter,
    editableContracts,
    isTrashView,
    dataMode,
    sortOrder,
  ]);

  const aggregatedContractsString = useMemo(() => JSON.stringify(aggregatedContracts), [aggregatedContracts]);

  useEffect(() => {
    const isSelectionValid =
      selectedContract &&
      aggregatedContracts.some((c) => c.id === selectedContract.id);

    if (!isSelectionValid && aggregatedContracts.length > 0) {
      setSelectedContract(aggregatedContracts[0]);
    } else if (aggregatedContracts.length === 0) {
      setSelectedContract(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregatedContractsString]);

  // クライアントサイドページネーション（Path A/B共通）
  const paginatedContracts = useMemo(() => {
    const startIndex = (clientCurrentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return aggregatedContracts.slice(startIndex, endIndex);
  }, [clientCurrentPage, itemsPerPage, aggregatedContracts]);

  // 表示用のtotalPagesとcurrentPageを計算
  const displayTotalPages = useMemo(() => {
    const totalCount = dataMode === 'paginated' && pagination?.totalCount !== null && pagination?.totalCount !== undefined
      ? pagination.totalCount
      : aggregatedContracts.length;

    const total = Math.ceil(totalCount / itemsPerPage);
    return total > 0 ? total : 1;
  }, [dataMode, pagination?.totalCount, aggregatedContracts.length, itemsPerPage]);

  const displayCurrentPage = useMemo(() => {
    const maxPage = Math.max(1, displayTotalPages);
    return Math.min(Math.max(1, clientCurrentPage), maxPage);
  }, [displayTotalPages, clientCurrentPage]);

  const paginatedContractsString = useMemo(() => JSON.stringify(paginatedContracts), [paginatedContracts]);

  // ページが変わったら、そのページの先頭データを選択（Path A/B共通）
  useEffect(() => {
    if (paginatedContracts.length > 0) {
      // 現在選択中の契約が表示ページ内にない場合、先頭を選択
      setSelectedContract(prev => {
        const isSelectedInPage = prev && paginatedContracts.some(c => c.id === prev.id);
        return isSelectedInPage ? prev : paginatedContracts[0];
      });
    }
  // selectedContract は関数型更新で参照するため依存配列から除外
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginatedContractsString]);

  // 表示位置計算ヘルパー
  const getPaginationDisplayText = useMemo(() => {
    // データがない場合は表示しない
    if (!aggregatedContracts || aggregatedContracts.length === 0) return null;

    const totalCount = dataMode === 'full'
      ? aggregatedContracts.length
      : (pagination?.totalCount ?? 0);

    if (totalCount === 0) return { fullText: 'データなし', range: '', total: '' };

    const currentPg = displayCurrentPage;
    const startItem = (currentPg - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPg * itemsPerPage, totalCount);

    return {
      range: `${startItem}-${endItem}件目`,
      total: `全${totalCount}件`,
      fullText: `${startItem}-${endItem}件目 / 全${totalCount}件`
    };
  }, [dataMode, aggregatedContracts, pagination?.totalCount, displayCurrentPage, itemsPerPage]);

  const handleNext = React.useCallback(() => {
    if (!selectedContract) return;
    const currentIndex = aggregatedContracts.findIndex(
      (c) => c.id === selectedContract.id
    );
    if (currentIndex < aggregatedContracts.length - 1) {
      const nextContract = aggregatedContracts[currentIndex + 1];
      setSelectedContract(nextContract);

      // Path Aの場合、次のデータが別のページにある場合はページを切り替え
      if (dataMode === 'full') {
        const nextIndex = currentIndex + 1;
        const nextPage = Math.floor(nextIndex / itemsPerPage) + 1;
        if (nextPage !== clientCurrentPage) {
          setClientCurrentPage(nextPage);
        }
      }

      if (pageScrollRef.current && detailContentRef.current) {
        const pRef = pageScrollRef.current;
        const dRef = detailContentRef.current;
        setTimeout(() => {
          if (pRef && dRef) {
            const targetScrollTop = dRef.offsetTop + dRef.clientHeight - pRef.clientHeight + 128;
            pRef.scrollTo({ top: targetScrollTop, behavior: 'auto' });
          }
        }, 0);
      }
    }
  }, [selectedContract, aggregatedContracts, dataMode, itemsPerPage, clientCurrentPage, pageScrollRef, detailContentRef]);

  const handlePrev = React.useCallback(() => {
    if (!selectedContract) return;
    const currentIndex = aggregatedContracts.findIndex(
      (c) => c.id === selectedContract.id
    );
    if (currentIndex > 0) {
      const prevContract = aggregatedContracts[currentIndex - 1];
      setSelectedContract(prevContract);

      // Path Aの場合、前のデータが別のページにある場合はページを切り替え
      if (dataMode === 'full') {
        const prevIndex = currentIndex - 1;
        const prevPage = Math.floor(prevIndex / itemsPerPage) + 1;
        if (prevPage !== clientCurrentPage) {
          setClientCurrentPage(prevPage);
        }
      }

      if (pageScrollRef.current && detailContentRef.current) {
        const pRef = pageScrollRef.current;
        const dRef = detailContentRef.current;
        setTimeout(() => {
          if (pRef && dRef) {
            const targetScrollTop = dRef.offsetTop + dRef.clientHeight - pRef.clientHeight + 128;
            pRef.scrollTo({ top: targetScrollTop, behavior: 'auto' });
          }
        }, 0);
      }
    }
  }, [selectedContract, aggregatedContracts, dataMode, itemsPerPage, clientCurrentPage, pageScrollRef, detailContentRef]);

  return {
    editableContracts,
    setEditableContracts,
    aggregatedContracts,
    selectedContract,
    setSelectedContract,
    clientCurrentPage,
    setClientCurrentPage,
    paginatedContracts,
    displayTotalPages,
    displayCurrentPage,
    getPaginationDisplayText,
    handleNext,
    handlePrev,
  };
};
